import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { addDoc, collection, doc, getDoc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { db } from '../firebase';
import './AdminParentsView.css';
import './SuperAdminDetailPages.css';

// Helper: load parents like iOS — query users for role=Parent by schoolId, then load each parent profile from parents collection by ID
async function loadParentsLikeIOS(schoolId, scoped) {
  const usersSnapshot = await getDocs(collection(db, 'users'));
  const sid = (schoolId || '').toLowerCase();
  const parentUsers = usersSnapshot.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(u => u.role === 'Parent')
    .filter(u => !sid || (u.schoolId || '').toLowerCase() === sid);

  const parents = [];
  for (const parentUser of parentUsers) {
    try {
      const parentDoc = await getDoc(doc(db, 'parents', parentUser.id));
      if (parentDoc.exists()) {
        parents.push({ id: parentDoc.id, ...parentDoc.data() });
      } else {
        // Fallback: use user data as parent profile if no parents doc exists
        parents.push({
          id: parentUser.id,
          firstName: parentUser.firstName || '',
          lastName: parentUser.lastName || '',
          fullName: `${parentUser.firstName || ''} ${parentUser.lastName || ''}`.trim(),
          email: parentUser.email || '',
          phoneNumber: parentUser.phoneNumber || '',
          linkedChildrenIds: parentUser.linkedChildrenIds || [],
          schoolId: parentUser.schoolId || null,
          isActive: parentUser.isActive !== false,
        });
      }
    } catch (e) {
      console.error('🔥 AdminParents: Error loading parent profile for', parentUser.id, e);
    }
  }
  return parents;
}

function SuperAdminParentsView({ adminData }) {
  const [saSchoolId, setSaSchoolId] = useState('');
  const [saSchools, setSaSchools] = useState([]);
  useEffect(() => {
    getDocs(collection(db, 'schools')).then(snap => setSaSchools(snap.docs.map(d => ({ id: d.id, ...d.data() })))).catch(() => {});
  }, []);
  adminData = useMemo(() => ({ ...(adminData || {}), schoolId: saSchoolId || adminData?.schoolId || null }), [adminData, saSchoolId]);
  const [parents, setParents] = useState([]);
  const [students, setStudents] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editParent, setEditParent] = useState(null);
  const [detailParent, setDetailParent] = useState(null);
  const [loading, setLoading] = useState(true);

  const [newParent, setNewParent] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    linkedChildrenIds: []
  });

  const loadParents = useCallback(async () => {
    console.log('🔥 AdminParents: Loading parents (iOS-style), schoolId:', adminData?.schoolId);
    try {
      const parentsData = await loadParentsLikeIOS(adminData?.schoolId, !!adminData);
      console.log('🔥 AdminParents: Parents loaded', parentsData.length, parentsData);
      setParents(parentsData);
    } catch (error) {
      console.error('🔥 AdminParents: Error loading parents:', error);
    } finally {
      setLoading(false);
    }
  }, [adminData?.schoolId]);

  const loadStudents = useCallback(async () => {
    console.log('🔥 AdminParents: Loading students, schoolId:', adminData?.schoolId);
    try {
      const studentsData = [];
      const classesQuery = collection(db, 'classes');
      const classesSnapshot = await getDocs(classesQuery);
      let classesData = classesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Super Admin: no school selected = national view, load all classes
      const sid = (adminData?.schoolId || '').toLowerCase();
      if (sid) {
        classesData = classesData.filter(c => (c.schoolId || '').toLowerCase() === sid);
      }
      for (const cls of classesData) {
        const snapshot = await getDocs(collection(db, 'classes', cls.id, 'students'));
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.isActive !== false) {
            studentsData.push({ id: doc.id, ...data });
          }
        });
      }
      console.log('🔥 AdminParents: Students loaded', studentsData.length, studentsData);
      setStudents(studentsData);
    } catch (error) {
      console.error('🔥 AdminParents: Error loading students:', error);
    }
  }, [adminData]);

  useEffect(() => {
    loadParents();
    loadStudents();
  }, [loadParents, loadStudents]);

  const getFilteredParents = () => {
    if (!searchText) {
      return parents;
    }
    return parents.filter(parent =>
      parent.fullName?.toLowerCase().includes(searchText.toLowerCase()) ||
      parent.email?.toLowerCase().includes(searchText.toLowerCase())
    );
  };

  // Resolve schoolId -> school name (match doc id, uuid field, or name; case-insensitive)
  const resolveSchoolName = (schoolId) => {
    if (!schoolId) return 'N/A';
    const sid = String(schoolId).toLowerCase();
    const match = saSchools.find(s =>
      String(s.id).toLowerCase() === sid ||
      String(s.uuid || '').toLowerCase() === sid ||
      String(s.name || '').toLowerCase() === sid
    );
    return match ? (match.name || match.id) : String(schoolId);
  };

  const getLinkedChildren = (parent) => {
    if (!parent.linkedChildrenIds || parent.linkedChildrenIds.length === 0) {
      return [];
    }
    const ids = parent.linkedChildrenIds.map(id => String(id).toLowerCase());
    return students.filter(student =>
      ids.includes(String(student.studentId || '').toLowerCase()) ||
      ids.includes(String(student.id || '').toLowerCase()) ||
      ids.includes(String(student.uuid || '').toLowerCase())
    );
  };

  const handleAddParent = async (e) => {
    e.preventDefault();
    try {
      const parentData = {
        ...newParent,
        fullName: `${newParent.firstName} ${newParent.lastName}`,
        isActive: true,
        createdAt: new Date().toISOString(),
        schoolId: adminData?.schoolId || null
      };
      await addDoc(collection(db, 'parents'), parentData);
      setShowAddForm(false);
      setNewParent({ firstName: '', lastName: '', email: '', phoneNumber: '', linkedChildrenIds: [] });
      loadParents();
    } catch (error) {
      console.error('Error adding parent:', error);
      alert('Failed to add parent');
    }
  };

  const handleEditClick = (parent) => {
    setEditParent({
      ...parent,
      linkedChildrenIds: parent.linkedChildrenIds || []
    });
    setIsEditing(true);
  };

  const handleCloseModal = () => {
    setIsEditing(false);
    setEditParent(null);
  };

  const handleUpdateParent = async (e) => {
    e.preventDefault();
    if (!editParent) return;
    try {
      // Only name/email/phone are editable — linked children are read-only for admins
      await updateDoc(doc(db, 'parents', editParent.id), {
        fullName: `${editParent.firstName || ''} ${editParent.lastName || ''}`.trim(),
        firstName: editParent.firstName,
        lastName: editParent.lastName,
        email: editParent.email,
        phoneNumber: editParent.phoneNumber,
        updatedAt: new Date().toISOString()
      });
      handleCloseModal();
      loadParents();
    } catch (error) {
      console.error('Error updating parent:', error);
      alert('Failed to update parent');
    }
  };

  const handleDeleteParent = async (parentId) => {
    if (!window.confirm('Are you sure you want to delete this parent?')) return false;
    try {
      await updateDoc(doc(db, 'parents', parentId), { isActive: false });
      loadParents();
      return true;
    } catch (error) {
      console.error('Error deleting parent:', error);
      alert('Failed to delete parent');
      return false;
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const filteredParents = getFilteredParents();

  // Full-screen parent detail page — replaces the list (not an overlay)
  if (detailParent) {
    const p = detailParent;
    const linkedChildren = getLinkedChildren(p);
    return (
      <div className="detail-page">
        <button className="dp-back" onClick={() => setDetailParent(null)}>
          ◄ Back to Parents
        </button>

        <div className="dp-hero">
          <div className="dp-avatar">👨‍👩‍👧</div>
          <div className="dp-hero-text">
            <h2>{p.fullName || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Unknown'}</h2>
            <p>{p.email || 'No email'}</p>
            <span className={`dp-badge ${p.isActive === false ? 'red' : 'green'}`}>
              {p.isActive === false ? 'Inactive' : 'Active'}
            </span>
            <span className="dp-badge blue">{linkedChildren.length} linked {linkedChildren.length === 1 ? 'child' : 'children'}</span>
          </div>
        </div>

        <div className="dp-card">
          <h3>Parent Information</h3>
          <div className="dp-grid">
            <div className="dp-field">
              <div className="dp-label">Name</div>
              <div className="dp-value">{p.fullName || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'N/A'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Email</div>
              <div className="dp-value">{p.email || 'N/A'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Phone</div>
              <div className="dp-value">{p.phoneNumber || 'N/A'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Username</div>
              <div className="dp-value">{p.username || 'N/A'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">School</div>
              <div className="dp-value">{resolveSchoolName(p.schoolId)}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Status</div>
              <div className="dp-value">{p.isActive === false ? 'Inactive' : 'Active'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Created</div>
              <div className="dp-value">{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : 'N/A'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Parent ID</div>
              <div className="dp-value">{p.id}</div>
            </div>
          </div>
        </div>

        <div className="dp-card">
          <h3>Linked Children ({linkedChildren.length})</h3>
          {linkedChildren.length === 0 ? (
            <p className="dp-empty">No linked children</p>
          ) : (
            linkedChildren.map(child => (
              <div key={child.id} className="dp-row">
                <span className="dp-row-main">{child.fullName || `${child.firstName || ''} ${child.lastName || ''}`.trim() || 'Unknown'}</span>
                <span className="dp-row-sub">{child.studentId || ''}</span>
              </div>
            ))
          )}
        </div>

        <div className="dp-actions">
          <button
            type="button"
            className="dp-btn-primary"
            onClick={() => { const parent = p; setDetailParent(null); handleEditClick(parent); }}
          >
            Edit
          </button>
          <button
            type="button"
            className="dp-btn-danger"
            onClick={async () => { if (await handleDeleteParent(p.id)) setDetailParent(null); }}
          >
            Delete
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-parents-view">
      <div className="view-header">
        <h2>Parents</h2>
        <div className="sa-school-filter"><label>School:</label><select value={saSchoolId} onChange={(e) => setSaSchoolId(e.target.value)}><option value="">All Schools</option>{saSchools.map(s => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}</select></div>
        <button className="add-button" onClick={() => setShowAddForm(true)}>
          + Add Parent
        </button>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search parents..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>

      <p className="count-text">Parents: {filteredParents.length}</p>

      {showAddForm && (
        <div className="add-parent-form">
          <h3>Create New Parent</h3>
          <form onSubmit={handleAddParent}>
            <div className="form-row">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={newParent.firstName}
                  onChange={(e) => setNewParent({ ...newParent, firstName: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={newParent.lastName}
                  onChange={(e) => setNewParent({ ...newParent, lastName: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={newParent.email}
                  onChange={(e) => setNewParent({ ...newParent, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={newParent.phoneNumber}
                  onChange={(e) => setNewParent({ ...newParent, phoneNumber: e.target.value })}
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
              <button type="submit">Create Parent</button>
            </div>
          </form>
        </div>
      )}

      {filteredParents.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">👨‍👩‍👧</div>
          <h3>No Parents</h3>
          <p>No parents found</p>
        </div>
      ) : (
        <div className="parents-list">
          {filteredParents.map(parent => {
            const linkedChildren = getLinkedChildren(parent);
            return (
              <div key={parent.id} className="parent-card" onClick={() => setDetailParent(parent)}>
                <div className="parent-avatar">
                  👨‍👩‍👧
                </div>
                <div className="parent-info">
                  <h4>{parent.fullName}</h4>
                  <p>{parent.email}</p>
                  {parent.phoneNumber && <p>📞 {parent.phoneNumber}</p>}
                  <div className="linked-children">
                    <span className="children-count">{linkedChildren.length} linked children</span>
                    {linkedChildren.length > 0 && (
                      <div className="children-list">
                        {linkedChildren.map(child => (
                          <span key={child.id} className="child-tag">
                            {child.fullName}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="parent-status">
                  <span className="status-dot active"></span>
                </div>
                <button
                  className="delete-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteParent(parent.id);
                  }}
                >
                  🗑️
                </button>
              </div>
            );
          })}
        </div>
      )}

      {isEditing && editParent && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Parent</h3>
            <form onSubmit={handleUpdateParent}>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={editParent.firstName || ''}
                    onChange={(e) => setEditParent({ ...editParent, firstName: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={editParent.lastName || ''}
                    onChange={(e) => setEditParent({ ...editParent, lastName: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={editParent.email || ''}
                    onChange={(e) => setEditParent({ ...editParent, email: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    value={editParent.phoneNumber || ''}
                    onChange={(e) => setEditParent({ ...editParent, phoneNumber: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="cancel-button" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="save-button">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

export default SuperAdminParentsView;
